import * as classNames from "classnames";
import { debounce } from "lodash";
import * as React from "react";

import { RichMarkdown, MarkdownSupport, Msg } from "@shared/components";
import Message from "@shared/services/message";

interface EditorAppealFormProps {
  descriptionKey?: string;
  isUpdating: boolean;
  isUpdateFail: boolean;
  isUpdateSuccess: boolean;
  value: string;
  titleClass?: string;
  titleKey?: string;
  wait?: number;
  onChange: (x: string) => void;
}

export const EditorAppealForm: React.FunctionComponent<
  EditorAppealFormProps
> = ({
  descriptionKey,
  isUpdating,
  isUpdateFail,
  isUpdateSuccess,
  value,
  titleClass,
  titleKey = "appeal.yourComment",
  wait = 1000,
  onChange,
}: EditorAppealFormProps) => {
  const [appealComment, setAppealComment] = React.useState(value);

  const [showMarkdownUpdate, setShowMarkdownUpdate] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | undefined>();

  // Prevent recreation of the debounce function on every render
  const debounceChange = React.useRef(
    debounce((newValue) => {
      onChange(newValue);
    }, wait),
  ).current;

  const markDownPlaceHolder = Message.getMessageByKey("appeal.placeholder");
  const saveIndicatorKey = isUpdating
    ? "message.applicant.appeal.updating"
    : isUpdateSuccess
    ? "message.applicant.appeal.saved"
    : "message.applicant.appeal.failure";
  const markDownStatusClass = classNames(
    "editor-appeal-form__markdown-status",
    { green: isUpdateSuccess, red: isUpdateFail },
  );

  React.useEffect(() => {
    if (isUpdating) {
      setShowMarkdownUpdate(true);
    } else if (isUpdateSuccess || isUpdateFail) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setShowMarkdownUpdate(false);
      }, 5000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isUpdating, isUpdateFail, isUpdateSuccess]);

  const handleOnChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const newValue = event.currentTarget.value;
    setAppealComment(newValue);
    debounceChange(newValue);
  };

  return (
    <div className="editor-appeal-form">
      <div className="editor-appeal-form__header">
        <div className={titleClass}>
          <Msg id={titleKey} />
        </div>
        <MarkdownSupport />
      </div>
      {descriptionKey && (
        <div className="editor-appeal-form__description">
          <Msg id={descriptionKey} />
        </div>
      )}
      <div className="editor-appeal-form__content">
        <RichMarkdown
          value={appealComment}
          onChange={handleOnChange}
          placeholder={markDownPlaceHolder}
        />
      </div>
      <div className="editor-appeal-form__footer">
        <div className={markDownStatusClass}>
          {showMarkdownUpdate && <Msg id={saveIndicatorKey} />}
        </div>
      </div>
    </div>
  );
};
